import * as Yup from 'yup';

import { optionalPasswordField } from './password-policy';
import { isValidEmail } from '../components/email-chips-input';

const usPhoneRegex = /^(?:\+1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

export const adminHomeLocationScheama = Yup.object({
    // Fields stay .optional() (NOT .nullable()): this schema's InferType is
    // exported as LocationAddress and consumed by non-form code (event map),
    // so widening it to include null broke compilation. Stored nulls that the
    // edit form loads are coerced to '' in SystemUserForm initialValues before
    // validation (#support 2026-07-29). The pom-2-api schema tolerates null
    // defensively via .nullish().transform.
    address: Yup.string().optional(),
    state: Yup.string().optional(),
    zip: Yup.string().optional(),
    city: Yup.string().optional(),
  }).optional();

export const createAdminFormSchema = Yup.object({
    id: Yup.string().optional(),
    // Validate email format (was .required() only, so a phone number passed —
    // #support 2026-07-29 "Add New POM Master"). Use a .test with isValidEmail
    // (which trims internally) rather than Yup .email(): these objects are
    // .strict(), so Yup skips .trim() transforms — a plain .email() would then
    // reject a legacy stored email with stray whitespace and block an unrelated
    // edit. .test runs in strict mode and matches the trimming pom-2-api schema.
    email: Yup
        .string()
        .required('Please provide the email of the admin user')
        .test('valid-email', 'Please provide a valid email address', (value) => !value || isValidEmail(value)),
    fullname: Yup
        .string()
        .required('Please provide the name of the admin user'),
    phone: Yup.string()
        .test('valid-phone', 'Phone number must have at least 10 digits', (value) => {
            if (!value) return false;
            const cleanedPhoneNumber = value.trim().replace(/\D/g, '');
            return cleanedPhoneNumber.length >= 10;
        })
        .required('Phone number is required'),
    userType: Yup.string().required('Please provide the type of the admin user'),
    grantIntakeRiskAssessment: Yup.boolean().optional(),
    teamIds: Yup.array(Yup.string()).optional(),
    groupIds: Yup.array(Yup.string()).optional(),
    alertSettings: Yup.object({
        sms: Yup.boolean().optional().default(true),
        call: Yup.boolean().optional().default(false),
        email: Yup.boolean().optional().default(false),
        push: Yup.boolean().optional().default(false),
    }).optional(),
    homeLocation: adminHomeLocationScheama,
}).test('role-assignment-validation', 'Invalid team/group assignments for selected role', function(value) {
    const { userType, teamIds, groupIds } = value;
    
    // VIEW_ONLY users cannot be assigned to teams or groups
    if (userType === 'VIEW_ONLY' && (teamIds?.length || groupIds?.length)) {
        return this.createError({
            message: 'View Only users cannot be assigned to teams or groups'
        });
    }
    
    // Team Leaders can only be assigned to teams, not groups
    if (userType === 'TEAM_LEADER' && groupIds?.length) {
        return this.createError({
            message: 'Team Leaders can only be assigned to teams, not groups'
        });
    }
    
    return true;
}).strict();

export const manageAdminPermissionsSchema = Yup.object({
    permissionFlags: Yup.array().required('Please, provide the permissions of the user').min(1, 'Please, select the permissions of the user')
}).strict();

export const updateAdminFormSchema = Yup.object({
    firstName: Yup.string().required('Please type your first name'),
    lastName: Yup.string().required('Please type your last name'),
    email: Yup.string().required('Please type your email').test('valid-email', 'Please type a valid email address', (value) => !value || isValidEmail(value)),
    // NIST 800-63B (C.1.5): length only, no forced composition. Server is the
    // source of truth and runs the breach check. Optional — blank keeps the
    // existing password (empty string is skipped, not range-checked).
    password: optionalPasswordField()
        .oneOf([Yup.ref('confirmPassword'), null], `Password doesn't match`),
    confirmPassword: optionalPasswordField()
        .oneOf([Yup.ref('password'), null], `Password doesn't match`),
}).strict();
